import type { ComponentPropsWithRef } from 'react';
import { Button } from '@/ui/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/shadcn/tooltip';
import { Icon } from './icons/Icon';
import type { IconName } from './icons/registry';

export type IconActionProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'aria-label'> & {
    readonly icon: IconName;
    /** The action's name: the button's accessible name and the tooltip's text. */
    readonly label: string;
    /** The tooltip's text when it should be shorter than the accessible name (*Email* for *Email Layla Haddad*). */
    readonly tip?: string;
};

/**
 * A small icon-only action on a row (the *Compact rows* layout of the UI revamp preview's Contacts card): the
 * shadcn ghost `icon-sm` Button with one glyph, named by `label`, and that name in a tooltip on hover and on
 * keyboard focus. The tooltip repeats the name and never describes the button a second time
 * (`describesTrigger={false}`). Takes a ref, so a dialog it opens can give focus back to it.
 */
export function IconAction({ icon, label, tip, ...props }: IconActionProps) {
    return (
        <Tooltip>
            <TooltipTrigger asChild describesTrigger={false}>
                <Button aria-label={label} size="icon-sm" type="button" variant="ghost" {...props}>
                    <Icon name={icon} size="sm" />
                </Button>
            </TooltipTrigger>
            <TooltipContent>{tip ?? label}</TooltipContent>
        </Tooltip>
    );
}
